// Applications page

import React, { useState, useCallback, useEffect, useMemo } from "react";
import { generatePath } from "react-router-dom";
import {
  AlertTriangle,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Globe,
  Plus,
  Trash2,
  X,
} from "lucide-react";

import UserAvatar from "@/components/common/UserAvatar";
import { Card, CardContent } from "@/components/ui/card";
import VerifyButton from "@/components/common/VerifyButton";
import SortSelect from "@/components/common/SortSelect";
import ConfirmModal from "@/components/common/ConfirmModal";
import { WebAppOperationsClass } from "@/models/webAppOperations";
import { deriveAppAbbrev, deriveAppColor } from "@/utils/appIdentity";
import { useAppViewScope } from "@/utils/useAppViewScope";
import { getTestResultLabel } from "@/utils/testStatusLabel";
import AppStatusPill from "@/components/common/AppStatusPill";
import { hasAppBeenTested } from "@/utils/webappTestStatus";
import { isWebAppTestRunning } from "@/utils/liveTestState";
import { useTenantEntitlements } from "@/utils/useTenantEntitlements";
import {
  EntitlementView,
  blockReasonMessage,
  entitlementDateQualifier,
} from "@/services/tenantEntitlements.service";
import { formatShortDate } from "@/utils/date";
import {
  getSeverityPillStyle,
  getSeverityLabel,
} from "@/utils/severityStyles";
import WebApplicationClass from "@/models/webApplication";
import UserClass from "@/models/user";
import {
  ADD_APP_PAGE,
  RISK_RATING_CRITICAL,
  RISK_RATING_HIGH,
  RISK_RATING_LOW,
  RISK_RATING_MED,
  SINGLE_WEBAPP_PAGE,
  TERMINAL_TEST_STATUSES,
} from "@/constants/definitions";

// Status pill labels + colors come from the shared
// getTestResultLabel helper (src/utils/testStatusLabel.ts). Used to
// be a local PHASE_STYLES map that mirrored Dashboard's — they drifted
// (this one missed the Test_Failed entry) and rendered the same test
// in different colors across the two pages. Centralising removes the
// drift surface entirely.

const APPS_PER_PAGE = 50;

const SORT_ALPHABETICAL = "Alphabetical";
const SORT_HIGHEST_RISK = "Highest Risk";
const SORT_LOWEST_RISK = "Lowest Risk";
const SORT_LATEST_TEST = "Latest Test";
const SORT_OLDEST_TEST = "Oldest Test";

const SORT_OPTIONS: { value: string; label: string }[] = [
  { value: SORT_ALPHABETICAL,  label: SORT_ALPHABETICAL },
  { value: SORT_HIGHEST_RISK,  label: SORT_HIGHEST_RISK },
  { value: SORT_LOWEST_RISK,   label: SORT_LOWEST_RISK },
  { value: SORT_LATEST_TEST,   label: SORT_LATEST_TEST },
  { value: SORT_OLDEST_TEST,   label: SORT_OLDEST_TEST },
];

const DEFAULT_SORT = SORT_ALPHABETICAL;

// Single point of truth for applying a named sort to a list of webapps.
// Reused by the initial useState seed, the useEffect that reseeds on
// listOfWebapps prop changes, and the user-triggered sort handler.
function applyAppSort(
  webappOps: WebAppOperationsClass,
  sourceApps: WebApplicationClass[],
  sortType: string,
): WebApplicationClass[] {
  const copy = [...sourceApps];
  if (sortType === SORT_ALPHABETICAL) return webappOps.sortByWebappName(copy);
  if (sortType === SORT_HIGHEST_RISK) return webappOps.sortHighestRisk(copy);
  if (sortType === SORT_LOWEST_RISK)  return webappOps.sortLowestRisk(copy);
  if (sortType === SORT_LATEST_TEST)  return webappOps.sortByYoungestTest(copy);
  if (sortType === SORT_OLDEST_TEST)  return webappOps.sortByOldestTest(copy);
  return copy;
}

interface ApplicationsProps {
  subscriptionClient: any;
  user: UserClass;
  cognitologin: any;
  listOfWebapps: WebApplicationClass[];
  allUsers: UserClass[];
  updateListOfApps: (apps: WebApplicationClass[]) => void;
  verifyApp: (webappid: string) => void;
  updateCurrentWebAppCallback: (webappid: string) => void;
  navigate: (path: string) => void;
  pixelWidth?: string;
}

export default function Applications({
  subscriptionClient,
  user,
  listOfWebapps,
  allUsers,
  updateListOfApps,
  verifyApp,
  updateCurrentWebAppCallback,
  navigate,
}: ApplicationsProps) {
  const webappOps = useMemo(() => new WebAppOperationsClass(), []);
  // Per-app license lookups (quota is per app). Fetched once for the page;
  // each row reads its own license by web-app id.
  const { entitlementForWebApp } = useTenantEntitlements();

  const [apps, setApps] = useState<WebApplicationClass[]>(
    () => applyAppSort(webappOps, listOfWebapps, DEFAULT_SORT)
  );
  const [currentPage, setCurrentPage] = useState(0);
  const [sortKey, setSortKey] = useState<string>(DEFAULT_SORT);
  // My Apps / All Apps toggle — persisted per-user in localStorage so
  // the choice carries between the Applications page and the Dashboard
  // and survives reloads.
  const [displayMyApps, setDisplayMyApps] = useAppViewScope(user.emailaddress);
  const [showUnverified, setShowUnverified] = useState(false);

  // App pending deletion — null when no confirm dialog is open. We keep
  // the whole instance (not just the id) so the modal can name it in the
  // confirmation message without re-finding it.
  const [appPendingDelete, setAppPendingDelete] = useState<WebApplicationClass | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  // Re-seed `apps` whenever the listOfWebapps prop reference changes
  // (e.g. App.tsx bumped the array after a live event, or a new app
  // was enrolled). Without this, apps was a one-shot snapshot — newly
  // added apps and risk-score-driven re-orders never made it into the
  // displayed list until the user manually changed the sort.
  // Preserves the user's current sortKey.
  useEffect(() => {
    setApps(applyAppSort(webappOps, listOfWebapps, sortKey));
  }, [listOfWebapps, sortKey, webappOps]);

  const displayedApps = useMemo(() => {
    if (displayMyApps && user.isUserAdmin()) {
      return apps.filter((app) => app.owner === user.emailaddress);
    }
    return apps;
  }, [apps, displayMyApps, user]);

  const unverifiedApps = useMemo(() => {
    if (displayMyApps && user.isUserAdmin()) {
      return webappOps.getUnverifiedAppsList(listOfWebapps, user.emailaddress);
    }
    return webappOps.getUnverifiedAppsList(listOfWebapps);
  }, [listOfWebapps, displayMyApps, user, webappOps]);

  const totalPages = Math.ceil(displayedApps.length / APPS_PER_PAGE);
  const startIndex = currentPage * APPS_PER_PAGE;
  const pageApps = displayedApps.slice(startIndex, startIndex + APPS_PER_PAGE);

  // Empty-state variants:
  //  - hasNoApps: nothing to show in the current view.
  //  - isFilteredEmpty: the view is empty only because the My-Apps
  //    filter is active — the organization does have applications. This
  //    lets an admin who owns none see "view all apps" guidance instead
  //    of the misleading "enrol your first app" copy for an app that
  //    already exists under another owner.
  const hasNoApps = displayedApps.length === 0;
  const isFilteredEmpty = hasNoApps && listOfWebapps.length > 0;

  const handlePageChange = useCallback((newPage: number) => {
    setCurrentPage(newPage);
  }, []);

  const handleSort = useCallback(
    (sortType: string) => {
      setApps(applyAppSort(webappOps, listOfWebapps, sortType));
      setSortKey(sortType);
      setCurrentPage(0);
    },
    [listOfWebapps, webappOps]
  );

  const openApp = useCallback(
    (webappid: string) => {
      updateCurrentWebAppCallback(webappid);
      navigate(generatePath(SINGLE_WEBAPP_PAGE, { webappid }));
    },
    [updateCurrentWebAppCallback, navigate]
  );

  // ---- Delete app ----
  //
  // Opens the destructive confirm modal. Blocked if a test is currently
  // running on the app — deleting an in-flight test would orphan worker
  // resources on the backend, so we surface a clear message and require
  // the user to stop the test first.
  const requestDeleteApp = useCallback((targetApp: WebApplicationClass) => {
    setDeleteError(null);
    if (isWebAppTestRunning(targetApp)) {
      setDeleteError(
        `Cannot delete ${targetApp.name} while a test is running on it. Stop the test first.`,
      );
      // Auto-dismiss the inline error after a few seconds so a later
      // delete attempt isn't shadowed by a stale message.
      setTimeout(() => setDeleteError(null), 6000);
      return;
    }
    setAppPendingDelete(targetApp);
  }, []);

  const cancelDeleteApp = useCallback(() => {
    setAppPendingDelete(null);
  }, []);

  const confirmDeleteApp = useCallback(() => {
    if (!appPendingDelete) return;
    const deletedId = appPendingDelete.id;

    // Fire-and-forget — WebApplicationClass.delete() reaches the
    // backend and resets its own fields on success. We optimiztically
    // remove the app from the parent list so the row disappears
    // immediately (which is itself the visible confirmation).
    appPendingDelete.delete();
    updateListOfApps(listOfWebapps.filter((entry) => entry.id !== deletedId));
    setAppPendingDelete(null);
  }, [appPendingDelete, listOfWebapps, updateListOfApps]);

  const getOwnerUser = useCallback(
    (ownerEmail: string): UserClass | null => {
      return allUsers.find((u) => u.emailaddress === ownerEmail) ?? null;
    },
    [allUsers]
  );

  return (
    <div className="min-h-[calc(100vh-4rem)] bg-gray-50">
      <div className="mx-auto max-w-6xl px-4 sm:px-6 py-8">

        {/* Header — title and app count on the left, My-Apps / All-Apps
            toggle on the right. Usage is not summarized here: quota is
            per app, so the remaining-pentests cue lives on each row
            (see ApplicationRow) rather than as a tenant-wide pill. */}
        <div className="flex items-center justify-between gap-3 flex-wrap mb-6">
          <div className="flex items-center gap-3 flex-wrap">
            <div>
              <h1 className="text-2xl font-semibold text-gray-900">Applications</h1>
              <p className="mt-1 text-sm text-gray-500">
                {displayedApps.length} application{displayedApps.length !== 1 ? "s" : ""}
              </p>
            </div>
          </div>
          {user.isUserAdmin() && (
            <div className="flex items-center rounded-lg border border-gray-200 bg-gray-100 p-0.5 gap-0.5">
              {[
                { label: "My Apps", value: true },
                { label: "All Apps", value: false },
              ].map(({ label, value }) => (
                <button
                  key={label}
                  type="button"
                  onClick={() => {
                    setDisplayMyApps(value);
                    setCurrentPage(0);
                  }}
                  className={[
                    "rounded-md px-3 py-1.5 text-sm font-medium transition-all",
                    displayMyApps === value
                      ? "bg-white text-gray-900 shadow-sm"
                      : "text-gray-500 hover:text-gray-700",
                  ].join(" ")}
                >
                  {label}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Inline error banner for delete attempts blocked because a
            test is in flight. Self-dismisses after a few seconds; the
            user can also click the X. */}
        {deleteError && (
          <div className="mb-4 flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3">
            <AlertTriangle className="h-4 w-4 text-amber-500 shrink-0 mt-0.5" />
            <p className="text-sm text-amber-700 flex-1">{deleteError}</p>
            <button
              type="button"
              onClick={() => setDeleteError(null)}
              className="text-amber-400 hover:text-amber-600 shrink-0"
              aria-label="Dismiss"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        )}

        {/* Unverified apps banner */}
        {unverifiedApps.length > 0 && (
          <div className="mb-4 rounded-lg border border-blue-200 bg-blue-50">
            <button
              type="button"
              onClick={() => setShowUnverified((previous) => !previous)}
              className="flex w-full items-center gap-3 px-4 py-3 text-left"
            >
              <AlertTriangle className="h-4 w-4 text-blue-500 shrink-0" />
              <span className="text-sm font-medium text-blue-700 flex-1">
                {unverifiedApps.length} unverified app{unverifiedApps.length !== 1 ? "s" : ""} — ownership not yet confirmed
              </span>
              <ChevronDown
                className={`h-4 w-4 text-blue-500 transition-transform ${showUnverified ? "rotate-180" : ""}`}
              />
            </button>
            {showUnverified && (
              <div className="border-t border-blue-200 divide-y divide-blue-100">
                {unverifiedApps.map((app) => (
                  <div key={app.id} className="flex items-center gap-3 px-4 py-3">
                    <div
                      className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-xs font-bold text-white"
                      style={{ backgroundColor: app.color || deriveAppColor(app.name) }}
                    >
                      {app.abbrev || deriveAppAbbrev(app.name)}
                    </div>
                    <span className="text-sm font-medium flex-1">{app.name}</span>
                    <div className="flex items-center gap-2">
                      <VerifyButton
                        webappid={app.id}
                        webapp={app}
                        appVerifiedSuccess={verifyApp}
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Main card */}
        <Card>
          {/* Toolbar — hidden when there are no applications to show;
              a sort control with nothing to sort reads as clutter above
              the empty state. */}
          {!hasNoApps && (
            <div className="flex items-center justify-end px-6 py-3 border-b border-gray-100">
              <SortSelect<string>
                id="apps-sort"
                value={sortKey}
                onChange={handleSort}
                options={SORT_OPTIONS}
                ariaLabel="Sort applications"
              />
            </div>
          )}

          {/* App list */}
          <CardContent className="p-0">
            {hasNoApps ? (
              <div className="px-6 py-16 text-center">
                <Globe className="mx-auto h-10 w-10 text-gray-300" />
                {isFilteredEmpty ? (
                  <>
                    <p className="mt-3 text-sm font-medium text-gray-700">
                      No applications you own
                    </p>
                    <p className="mt-1 text-sm text-gray-500">
                      None of your organization's applications are assigned to you yet.
                    </p>
                    <button
                      type="button"
                      onClick={() => {
                        setDisplayMyApps(false);
                        setCurrentPage(0);
                      }}
                      className="mt-4 inline-flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                    >
                      View all applications
                    </button>
                  </>
                ) : (
                  <>
                    <p className="mt-3 text-sm font-medium text-gray-700">
                      No applications registered yet
                    </p>
                    <p className="mt-1 text-sm text-gray-500">
                      Enrol your first web application to begin testing.
                    </p>
                    {user.isUserAdmin() && (
                      <button
                        type="button"
                        onClick={() => navigate(ADD_APP_PAGE)}
                        className="mt-4 inline-flex items-center gap-1.5 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-500 transition-colors"
                      >
                        <Plus className="h-4 w-4" />
                        Add Application
                      </button>
                    )}
                  </>
                )}
              </div>
            ) : (
              <div className="flex flex-col gap-4 p-4">
                {pageApps.map((app) => {
                  const ownerUser = user.isUserAdmin()
                    ? getOwnerUser(app.owner)
                    : null;
                  return (
                    <ApplicationRow
                      key={app.id}
                      app={app}
                      ownerUser={ownerUser}
                      showOwner={user.isUserAdmin()}
                      entitlement={entitlementForWebApp(app.id)}
                      onOpenApp={openApp}
                      canDelete={user.isUserAdmin()}
                      onDeleteApp={requestDeleteApp}
                    />
                  );
                })}
              </div>
            )}
          </CardContent>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-6 py-3 border-t border-gray-100">
              <p className="text-sm text-gray-500">
                Showing {startIndex + 1}–{Math.min(startIndex + APPS_PER_PAGE, displayedApps.length)} of {displayedApps.length}
              </p>
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 0}
                  className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600 disabled:opacity-30 disabled:hover:bg-transparent"
                >
                  <ChevronLeft className="h-4 w-4" />
                </button>
                {Array.from({ length: totalPages }, (_, pageNumber) => (
                  <button
                    key={pageNumber}
                    type="button"
                    onClick={() => handlePageChange(pageNumber)}
                    className={[
                      "rounded-lg px-3 py-1 text-sm transition-colors",
                      currentPage === pageNumber
                        ? "bg-blue-600 text-white"
                        : "text-gray-500 hover:bg-gray-100",
                    ].join(" ")}
                  >
                    {pageNumber + 1}
                  </button>
                ))}
                <button
                  type="button"
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages - 1}
                  className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600 disabled:opacity-30 disabled:hover:bg-transparent"
                >
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            </div>
          )}
        </Card>
      </div>

      {/* Destructive confirm dialog for app deletion. The message names
          the app and the blast radius so the user is making an informed
          decision. */}
      <ConfirmModal
        show={!!appPendingDelete}
        title="Delete this application?"
        message={
          appPendingDelete ? (
            <>
              <p>
                <span className="font-semibold text-gray-900">{appPendingDelete.name}</span>{" "}
                and all its data will be permanently removed:
              </p>
              <ul className="mt-2 list-disc pl-5 text-gray-600 space-y-0.5">
                <li>All test results and reports</li>
                <li>All vulnerabilities and remediation history</li>
                <li>All configured integrations for this application</li>
                <li>All path scope and test-account settings</li>
              </ul>
              <p className="mt-3 text-sm font-medium text-red-700">
                This action cannot be undone.
              </p>
            </>
          ) : null
        }
        confirmLabel="Delete Application"
        cancelLabel="Cancel"
        destructive
        onConfirm={confirmDeleteApp}
        onCancel={cancelDeleteApp}
      />
    </div>
  );
}

// ----------------------------------------
//   ApplicationRow
// ----------------------------------------

interface ApplicationRowProps {
  app: WebApplicationClass;
  ownerUser: UserClass | null;
  showOwner: boolean;
  // Per-app license view, or null for a legacy/unbilled app (no chip shown).
  entitlement: EntitlementView | null;
  onOpenApp: (webappid: string) => void;
  // Delete is permission-gated by the parent. Pentesters get `canDelete=false`
  // and the icon is hidden — the destructive mutation is also rejected
  // server-side as a defense in depth.
  canDelete: boolean;
  onDeleteApp: (app: WebApplicationClass) => void;
}

function ApplicationRow({
  app,
  ownerUser,
  showOwner,
  entitlement,
  onOpenApp,
  canDelete,
  onDeleteApp,
}: ApplicationRowProps) {
  const appColor   = app.color || deriveAppColor(app.name);
  const appAbbrev  = app.abbrev || deriveAppAbbrev(app.name);
  const status     = app.webAppTestStatus;
  // Single source of truth for "is a run in flight?" — see
  // utils/liveTestState.ts. Keeps this row, the Dashboard card, and the
  // webapp page in lockstep by construction.
  const isRunning  = isWebAppTestRunning(app);
  const isVerified = app.ownershipVerified;
  // Status pill label + tone derive from the latest terminal test via
  // the shared helper — agrees with the Dashboard card and the
  // WebApplication page TestDetails badge for the same test data.
  const latestTerminal = app.tests?.find(
    (test) =>
      !!test.testStatus && TERMINAL_TEST_STATUSES.has(test.testStatus),
  );
  const resultLabel = getTestResultLabel({
    testStatus: latestTerminal?.testStatus,
    endTrigger: latestTerminal?.endTrigger,
  });
  const openCrits  = status?.opencrits ?? 0;
  const openHighs  = status?.openhighs ?? 0;
  const openMeds   = status?.openmeds  ?? 0;
  const openLows   = status?.openlows  ?? 0;

  // Shared predicate — same definition as Dashboard ApplicationCard.
  // Was previously inlined per page and drifted; src/utils/
  // webappTestStatus.ts is now the single source.
  const hasTested = hasAppBeenTested(app);

  const hasNoOpenFindings =
    isVerified && hasTested
    && openCrits === 0 && openHighs === 0 && openMeds === 0 && openLows === 0;

  return (
    // The entire row is a click target so users can click anywhere
    // (owner column, empty space, badge) to open the app — not just
    // the name/URL/severity area. The inner <button> stays for keyboard
    // accessibility (tab focus + Enter), and its click event bubbles
    // here harmlessly since both handlers navigate to the same place.
    // The trash button stops propagation so destructive intent doesn't
    // also trigger a navigation.
    <div
      onClick={() => onOpenApp(app.id)}
      className="flex items-start gap-5 px-6 py-5 rounded-xl bg-white border border-gray-200 shadow-sm transition-shadow hover:shadow-md cursor-pointer"
    >
      {/* App badge */}
      <div
        className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg text-sm font-bold text-white mt-0.5"
        style={{ backgroundColor: appColor }}
      >
        {appAbbrev}
      </div>

      {/* Name + URL + status pill + severity pills — clickable area.
          The status pill lives here (next to the app's other state info)
          rather than on the far right of the row, so it doesn't read as
          if it belongs to the owner column beside it.
          On lg+ this column is pinned to half the row width so the
          row reads as ~1/2 main content / 1/4 owner / 1/4 trash. Below
          lg the owner is hidden, so the main content reverts to
          flex-1 and fills the remaining space. */}
      <button
        type="button"
        onClick={() => onOpenApp(app.id)}
        className="min-w-0 flex-1 lg:flex-none lg:w-1/2 text-left"
      >
        <div className="flex items-center gap-2 min-w-0">
          <p className="text-base font-semibold text-gray-900 truncate">{app.name}</p>
          <AppStatusPill
            isVerified={isVerified}
            hasTested={hasTested}
            isRunning={isRunning}
            resultLabel={resultLabel}
            className="shrink-0"
          />
        </div>
        {app.url && (
          <p className="mt-1 text-sm text-gray-500 truncate">{app.url}</p>
        )}
        {/* Per-app license cue — present but not dominating. Amber when the
            license blocks a new pentest; otherwise a quiet remaining-count
            (plus the access-end date for a one-off). Nothing for a
            legacy/unbilled app (no license). */}
        {entitlement && (
          entitlement.blockReason ? (
            <p className="mt-1 text-xs font-medium text-amber-600 truncate">
              {blockReasonMessage(entitlement.blockReason)}
            </p>
          ) : (
            <p className="mt-1 text-xs text-gray-400 truncate">
              {entitlement.testsRemaining} of {entitlement.testsAllowed} pentest
              {entitlement.testsAllowed === 1 ? "" : "s"} left
              {entitlementDateQualifier(entitlement, formatShortDate)}
            </p>
          )
        )}
        {/* Severity pills — matches Dashboard ApplicationCard styling.
            min-h reserves height so rows align when there are no pills.
            Suppressed while a test is in flight: the counts come from the
            last completed test's (frozen) aggregate, and the "No open
            findings" pill would contradict the "Testing" status pill.
            Counts reappear once the test terminates. */}
        <div className="mt-3 flex min-h-[22px] items-center gap-1.5 flex-wrap">
          {!isRunning && (
            <>
              {[
                { severity: RISK_RATING_CRITICAL, count: openCrits },
                { severity: RISK_RATING_HIGH,     count: openHighs },
                { severity: RISK_RATING_MED,      count: openMeds  },
                { severity: RISK_RATING_LOW,      count: openLows  },
              ].map(({ severity, count }) => {
                if (count === 0) return null;
                const pill = getSeverityPillStyle(severity);
                return (
                  <span
                    key={severity}
                    className={`inline-flex items-center rounded-full ${pill.bg} px-2 py-0.5 text-[10px] font-semibold ${pill.text}`}
                  >
                    {count} {getSeverityLabel(severity)}
                  </span>
                );
              })}
              {hasNoOpenFindings && (
                <span className="inline-flex items-center rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-semibold text-emerald-700">
                  No open findings
                </span>
              )}
            </>
          )}
        </div>
      </button>

      {/* Owner (admin only, hidden in My Apps mode).
          Mini-column with a soft "Owner" label above the avatar + name.
          Sentence case (not uppercase + tracking) — the heavier
          column-header style works on Dashboard cards where it has
          surrounding whitespace, but in a single row it competes with
          the content. self-center anchors the column to the row's
          vertical centerline. */}
      {showOwner && (
        <div className="shrink-0 max-lg:!hidden w-1/4 self-center">
          <p className="text-xs text-gray-400">Owner</p>
          <div className="mt-1.5 flex items-center gap-2">
            {ownerUser ? (
              <>
                <UserAvatar
                  avatarUrl={ownerUser.avatar}
                  alt={ownerUser.displayName}
                  className="h-6 w-6 rounded-full object-cover ring-1 ring-gray-200 shrink-0"
                />
                <span className="text-sm text-gray-500 truncate">
                  {ownerUser.displayName || ownerUser.emailaddress}
                </span>
              </>
            ) : (
              <span className="text-sm text-gray-500">Unknown</span>
            )}
          </div>
        </div>
      )}

      {/* Delete icon — admin/owner only. Visible button surface
          (border + bg) so it reads as an action affordance. ml-auto
          pushes it to the far right of the row, claiming the
          remaining ~quarter as the destructive-action region — visually
          separated from the owner column so the two don't read as
          related. */}
      {canDelete && (
        <button
          type="button"
          onClick={(clickEvent) => {
            // Prevent the outer row click from also firing — clicking
            // the trash should open the delete confirmation, not
            // navigate to the app being deleted.
            clickEvent.stopPropagation();
            onDeleteApp(app);
          }}
          className="ml-auto self-center shrink-0 inline-flex items-center justify-center rounded-md border border-gray-200 bg-white p-2 text-gray-500 hover:text-red-600 hover:border-red-200 hover:bg-red-50 transition-colors cursor-pointer"
          aria-label={`Delete ${app.name}`}
          title="Delete application"
        >
          <Trash2 className="h-5 w-5" />
        </button>
      )}
    </div>
  );
}
